import React, { SetStateAction } from 'react'
import loadable from '@loadable/component'
import { ArticleComponentType } from '../../../shared/types'
import { PropertyType, PropertyUnitsType, MetaDataType, ComponentType } from './PropertyPageContent.utils'
import CeilaBrochureImg from '../../../assets/images/ciela-at-aera-heights.webp'

import PropertyPageSlider from '../../../components/PropertyPageSlider/PropertyPageSlider'
import { Link } from 'gatsby'

const ConstructionModal = loadable(() => import('../../../components/ConstructionModal/ConstructionModal'))
const ContentComponents = loadable(() => import('../../../components/ContentComponents/ContentComponents'))
const ComponentThirteen = loadable(() => import('../../../components/ContentComponents/ComponentThirteen/ComponentThirteen'))

type PropertyPageContentProps = {
  propertyComponents: ArticleComponentType[] | null
  property: PropertyType
  unit: PropertyUnitsType
  getVirtualTourImg: () => any
  brochureComponent: ArticleComponentType | null
  constructionImage: MetaDataType | null
  constructionImages: MetaDataType[]
  constructionTitle: string
  constructionDescription: string
  toggleModal: React.Dispatch<SetStateAction<boolean>>
  showModal: boolean
  brochureTableComponent: ComponentType
}

export default function PropertyPageContent({
  propertyComponents,
  property,
  unit,
  getVirtualTourImg,
  brochureComponent,
  constructionImage,
  constructionImages,
  constructionTitle,
  constructionDescription,
  toggleModal,
  showModal,
  brochureTableComponent
}: PropertyPageContentProps): JSX.Element {
  const isCielaHeights = property.slug === 'ciela-at-aera-heights'
  const cielaBrochure = 'https://brochure.ayalalandpremier.com/view/645609973/'
  const unitDetails = unit?.overviewDetails?.overviewDetails

  const interactiveBrochure = isCielaHeights ? cielaBrochure : unitDetails?.file ? unitDetails?.file : brochureComponent?.description
  const virtualTourImage = getVirtualTourImg()?.image || ''

  // g-5-h: robustly resolve virtual tour URL from any supported section
  const getFirstVirtualTourUrl = (u: any): string | undefined => {
    if (!u) return undefined

    const collect = (section: any, videoKeyCandidates: string[], panoramaKeyCandidates: string[]) => {
      const urls: string[] = []
      if (section?.videos && Array.isArray(section.videos)) {
        section.videos.forEach((item: any) => {
          for (const key of videoKeyCandidates) {
            const val = item?.[key]
            if (typeof val === 'string' && val.trim()) {
              urls.push(val.trim())
              break
            }
          }
        })
      }
      if (section?.panoramas && Array.isArray(section.panoramas)) {
        section.panoramas.forEach((item: any) => {
          for (const key of panoramaKeyCandidates) {
            const val = item?.[key]
            if (typeof val === 'string' && val.trim()) {
              urls.push(val.trim())
              break
            }
          }
        })
      }
      return urls
    }

    const candidates: string[] = []
    const videoKeys = ['video', 'videos']
    const panoKeys = ['panorama', 'panoramas']

    candidates.push(
      ...collect(u.floorPlans, videoKeys, panoKeys),
      ...collect(u.overviewDetails, videoKeys, panoKeys),
      ...collect(u.locationLandmarks, videoKeys, panoKeys),
      ...collect(u.constructionUpdates, videoKeys, panoKeys)
    )

    return candidates.find((url) => typeof url === 'string' && url.trim().length > 0)
  }

  const virtualTourVideo = getFirstVirtualTourUrl(unit)
  
  // Check if we have construction updates to display
  const hasConstructionUpdates = Array.isArray(constructionImages) && constructionImages.length > 0
  
  // Get last update date if available in the constructionUpdates object
  const lastUpdated = unit?.constructionUpdates?.lastUpdated
    ? new Date(unit.constructionUpdates.lastUpdated).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null

  // Prepare data for each construction image to ensure they have proper metadata
  const enhancedConstructionImages = constructionImages.map((image, index) => ({
    ...image,
    altText: image.altText || `Construction update for ${property?.name} - Image ${index + 1}`,
    description: image.description || (index === 0 && constructionTitle ? constructionTitle : '')
  }))

  return (
    <div className="property-page-content">
      <h1 style={{ display: 'none' }}>{property?.propertyDetails?.projectName}</h1>
      {virtualTourVideo && !(propertyComponents?.[0]?.componentType === 'COMPONENT-1') && (
        <div className="universal-virtual-tour-cta">
          <div className="icons__virtual-tour">
            <a href={virtualTourVideo} target="_blank" rel="noopener noreferrer" className="icons__virtual-tour--img">
              <img loading="lazy" src={require('../../../assets/images/virtual-tour.webp').default} alt="Virtual Tour Image" />
            </a>
            <a className="icons__virtual-tour--cta" href={virtualTourVideo} target="_blank" rel="noopener noreferrer">
              View Virtual Tour
            </a>
          </div>
        </div>
      )}
      <ContentComponents
        components={propertyComponents}
        noMarginTop={true}
        interactiveBrochure={interactiveBrochure}
        virtualTourImage={virtualTourImage}
        virtualTourVideo={virtualTourVideo}
      />
      <div className="brochure-fold">
        {property?.propertyDetails?.propertyType !== 'Townhouse'
          ? brochureTableComponent && <ComponentThirteen isContentOnly={true} component={brochureTableComponent} />
          : null}
      </div>

      {/* Construction Updates Section */}
      {hasConstructionUpdates && (
        <div className="construction-updates-section">
          <div className="construction-updates-container">
            <div className="construction-updates-content">
              <h2 className="construction-updates-title">{constructionTitle || 'Construction Updates'}</h2>
              
              {lastUpdated && (
                <p className="construction-updates-date">Last updated: {lastUpdated}</p>
              )}
              
              <p className="construction-updates-description">
                {constructionDescription || `View the latest construction progress for ${property?.name}.`}
              </p>
              
              <button 
                className="construction-updates-button" 
                onClick={() => toggleModal(true)}
                aria-label="View Construction Updates"
              >
                View Gallery
              </button>
            </div>
            
            <div className="construction-updates-image">
              {constructionImage?.image ? (
                <div className="preview-image-container" onClick={() => toggleModal(true)}>
                  <img 
                    loading="lazy" 
                    src={constructionImage.image} 
                    alt={constructionImage.altText || `Construction update preview for ${property?.name}`} 
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <div className="preview-overlay">
                    <span className="view-text">View Gallery</span>
                  </div>
                </div>
              ) : (
                <div className="construction-image-placeholder">
                  No preview image available
                </div>
              )}
              
              {hasConstructionUpdates && constructionImages.length > 1 && (
                <div className="preview-gallery-count">
                  +{constructionImages.length - 1} more images
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {unit?.floorPlans?.unitsFloorPlans?.[0]?.imageGallery ? (
        <PropertyPageSlider {...{ floorPlans: property?.propertyUnits ? unit?.floorPlans : [] }} />
      ) : null}

      {/* Construction Updates Modal */}
      <ConstructionModal 
        width="auto" 
        height="auto" 
        showModal={showModal} 
        toggleModal={toggleModal}
        images={enhancedConstructionImages}
        maxWidth="1200px"
      >
        {/* This content is a fallback and will only be used if there are no valid images */}
        <div className="construction-image-fallback">
          <h3>{constructionTitle || 'Construction Updates'}</h3>
          {lastUpdated && <p>Last updated: {lastUpdated}</p>}
          <p>{constructionDescription || `No construction images available for ${property?.name} at this time.`}</p>
        </div>
      </ConstructionModal>

      {property?.propertyDetails?.propertyType !== 'Townhouse' ? (
        <div className="bottom-links-container">
          {property?.propertyDetails?.propertyType !== 'Townhouse' ? (
            <div className="bottom-links">
              <div className="content-links">
                <Link to="/inquire/">
                  <p>Make an Inquiry</p>
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
